const MEETING_ID_PREFIX = 'Meeting ID:';

/**
 * Regular expression to match the References section in the response
 */
const REFERENCE_REGEX = /References:([\s\S]*?)(?:$|(?=\n\n))/;

/**
 * Regular expression to match "Meeting ID: trs#<id>" pattern
 */
const MEETING_ID_REGEX = new RegExp(`(${MEETING_ID_PREFIX}\\s*)trs#([a-zA-Z0-9_.-]+)`, 'g');

/**
 * Helper function to normalize a string by removing special characters
 * @param {string} str - The string to normalize
 * @returns {string} - The normalized string with only alphanumeric characters
 */
const normalizeString = (str) => {
  if (!str) return '';
  // Remove all non-alphanumeric characters
  return str.replace(/[^a-zA-Z0-9]/g, '');
};

/**
 * Parses the References section in the response and converts meeting IDs to links
 * Uses a single regex to directly extract and format meeting IDs
 * @param {string} text - The response text
 * @param {Array} selectedItems - Array of selected items containing callIds
 * @returns {string} - The response text with meeting IDs converted to links
 */
const parseReferenceSection = (text, selectedItems = []) => {
  if (!text || typeof text !== 'string') {
    return text;
  }

  // Extract callIds from selectedItems
  const callIds = selectedItems.map((item) => item.callId || '');

  // Check if there's a References section
  const referencesMatch = text.match(REFERENCE_REGEX);
  if (!referencesMatch) {
    return text;
  }

  const referencesSection = referencesMatch[0];
  const referencesContent = referencesMatch[1].trim();

  // Process the references content with the meeting ID regex
  const processedContent = referencesContent.replace(MEETING_ID_REGEX, (match, prefix, meetingId) => {
    const normalizedMeetingId = normalizeString(meetingId);
    const matchedCallId = callIds.find((callId) => normalizeString(callId) === normalizedMeetingId);
    const actualCallId = matchedCallId || meetingId;

    return `${prefix}<callid href="${actualCallId}">${actualCallId}</callid>`;
  });

  // Format the processed content as a markdown list
  let formattedReferencesSection = 'References:\n';

  // Split by line breaks to get individual entries
  let entries = processedContent
    .split(/\n/)
    .map((entry) => entry.trim())
    .filter((entry) => entry);

  // If no entries were found by splitting by line breaks, use the whole content
  if (entries.length === 0) {
    entries = [processedContent.trim()];
  }

  // Process each entry to ensure each Meeting ID is on its own line
  const processedEntries = [];

  entries.forEach((entry) => {
    // Check if the entry contains multiple Meeting IDs
    const hasMultipleIds =
      entry.includes(MEETING_ID_PREFIX) && entry.indexOf(MEETING_ID_PREFIX) !== entry.lastIndexOf(MEETING_ID_PREFIX);
    if (hasMultipleIds) {
      // Split by "Meeting ID:" and process each part
      const parts = entry.split(MEETING_ID_PREFIX);
      parts.forEach((part, index) => {
        if (index === 0 && part.trim() === '') {
          // Skip empty first part
          return;
        }
        if (part.trim()) {
          processedEntries.push(`${MEETING_ID_PREFIX}${part.trim()}`);
        }
      });
    } else {
      // Single Meeting ID or other content, add as is
      processedEntries.push(entry);
    }
  });

  // Add each processed entry as a markdown list item
  processedEntries.forEach((entry) => {
    formattedReferencesSection += `- ${entry}\n`;
  });

  // Replace the original references section with the formatted one
  return text.replace(referencesSection, formattedReferencesSection);
};

export { normalizeString };
export default parseReferenceSection;
